<div class="question-card" tabindex="0" role="group" [attr.aria-labelledby]="cardHeadingId">
    <!-- HEADER: pagination hint + close button -->
    <div class="card-header">
        <span class="lead-in" [id]="cardHeadingId">{{ batch?.leadIn }}</span>
        <span class="pager">{{ displayIndex }} of {{ totalQuestions }}</span>
        <button
            mat-icon-button
            type="button"
            class="close-btn"
            [disableRipple]="true"
            [disabled]="!canClose || submitting"
            [attr.aria-label]="canClose ? 'Close questions' : 'Answer required questions before closing'"
            (click)="onCloseClick()">
            <mat-icon svgIcon="icon-v2:x-bold"></mat-icon>
        </button>
    </div>

    <ng-container *ngIf="currentQuestion as q">
        <div class="prompt-row">
            <h3 class="prompt">{{ q.prompt }}</h3>
            <span class="required-flag" *ngIf="q.required">Required</span>
        </div>
        <p class="context" *ngIf="q.context">{{ q.context }}</p>

        <!-- BODY -->
        <div class="card-body" [ngSwitch]="effectiveKind(q)">
            <!-- SINGLE_SELECT -->
            <ng-container *ngSwitchCase="'single_select'">
                <ng-container *ngFor="let opt of optionsFor(q); let i = index; trackBy: trackByOptionValue">
                    <button
                        type="button"
                        class="option-row"
                        [class.is-selected]="isOptionSelected(q, opt)"
                        [class.is-focused]="focusedOptionIndex === i"
                        [disableRipple]="true"
                        (click)="onSingleSelectClick(q, opt); next()"
                        (mouseenter)="focusedOptionIndex = i">
                        <span class="row-num" *ngIf="i < 9">{{ i + 1 }}</span>
                        <span class="row-body">
                            <span class="row-label">{{ opt.label }}</span>
                            <span class="row-hint" *ngIf="opt.hint">{{ opt.hint }}</span>
                        </span>
                        <span class="recommended-badge" *ngIf="opt.recommended">Recommended</span>
                    </button>
                </ng-container>
                <ng-container *ngIf="q.allow_other">
                    <ng-container *ngTemplateOutlet="otherRow; context: { q: q }"></ng-container>
                </ng-container>
            </ng-container>

            <!-- MULTI_SELECT -->
            <ng-container *ngSwitchCase="'multi_select'">
                <ng-container *ngFor="let opt of optionsFor(q); let i = index; trackBy: trackByOptionValue">
                    <button
                        type="button"
                        class="option-row multi"
                        [class.is-selected]="isOptionSelected(q, opt)"
                        [class.is-focused]="focusedOptionIndex === i"
                        [disableRipple]="true"
                        (click)="onMultiSelectToggle(q, opt)"
                        (mouseenter)="focusedOptionIndex = i">
                        <span class="checkbox" aria-hidden="true">
                            <mat-icon
                                *ngIf="isOptionSelected(q, opt)"
                                svgIcon="icon-v2:check"></mat-icon>
                        </span>
                        <span class="row-body">
                            <span class="row-label">{{ opt.label }}</span>
                            <span class="row-hint" *ngIf="opt.hint">{{ opt.hint }}</span>
                        </span>
                        <span class="recommended-badge" *ngIf="opt.recommended">Recommended</span>
                    </button>
                </ng-container>
                <ng-container *ngIf="q.allow_other">
                    <ng-container *ngTemplateOutlet="otherRow; context: { q: q }"></ng-container>
                </ng-container>
                <p class="multi-hint" *ngIf="multiSelectionCount(q) > 0">
                    {{ multiSelectionCount(q) }} selected — Enter to confirm and continue
                </p>
            </ng-container>

            <!-- TEXT (also fallback for unknown kinds + *_select with no options) -->
            <ng-container *ngSwitchCase="'text'">
                <input
                    #freeFormInput
                    type="text"
                    class="text-input"
                    [ngModel]="state[q.id]?.text ?? ''"
                    (ngModelChange)="onTextInput(q, $event)"
                    (focus)="onInputFocus()"
                    (blur)="onInputBlur()" />
            </ng-container>

            <!-- NUMBER -->
            <ng-container *ngSwitchCase="'number'">
                <input
                    #freeFormInput
                    type="number"
                    class="text-input"
                    [ngModel]="state[q.id]?.text ?? ''"
                    (ngModelChange)="onTextInput(q, $event)"
                    (focus)="onInputFocus()"
                    (blur)="onInputBlur()" />
            </ng-container>

            <!-- DATE -->
            <ng-container *ngSwitchCase="'date'">
                <input
                    type="date"
                    class="text-input"
                    [ngModel]="state[q.id]?.text ?? ''"
                    (ngModelChange)="onTextInput(q, $event)"
                    (focus)="onInputFocus()"
                    (blur)="onInputBlur()" />
            </ng-container>
        </div>

        <!-- FOOTER: nav buttons only. Keyboard hints removed — most -->
        <!-- shortcuts in this card are not yet honored, so don't promise -->
        <!-- them. Icons use plain Phosphor classes; no custom CSS. -->
        <div class="card-footer">
            <div class="nav-buttons">
                <button
                    type="button"
                    mat-stroked-button
                    color="accent"
                    [disableRipple]="true"
                    [disabled]="currentIndex === 0 || submitting"
                    (click)="prev()"
                    aria-label="Previous question">
                    <i class="ph-bold ph-arrow-left" aria-hidden="true"></i>
                    Back
                </button>
                <button
                    type="button"
                    mat-flat-button
                    color="primary"
                    [disableRipple]="true"
                    [disabled]="!canAdvance || submitting"
                    (click)="next()"
                    [attr.aria-label]="isLastQuestion ? 'Submit answers' : 'Next question'">
                    <ng-container *ngIf="!isLastQuestion">
                        Next
                        <i class="ph-bold ph-arrow-right" aria-hidden="true"></i>
                    </ng-container>
                    <ng-container *ngIf="isLastQuestion">
                        Submit
                    </ng-container>
                </button>
            </div>
        </div>
    </ng-container>
</div>

<!-- Reusable "Something else" row used by both single & multi select -->
<ng-template #otherRow let-q="q">
    <div
        class="option-row other-row"
        [class.is-active]="state[q.id]?.otherActive"
        [class.is-focused]="isOtherRowFocused(q)"
        (mouseenter)="focusedOptionIndex = (q.options?.length ?? 0)">
        <span class="row-num" aria-hidden="true">+</span>
        <ng-container *ngIf="!state[q.id]?.otherActive">
            <button
                type="button"
                class="other-trigger"
                [disableRipple]="true"
                (click)="onActivateOther(q)">
                Something else…
            </button>
        </ng-container>
        <input
            *ngIf="state[q.id]?.otherActive"
            #freeFormInput
            type="text"
            class="other-input"
            placeholder="Type your answer"
            [ngModel]="state[q.id]?.otherText ?? ''"
            (ngModelChange)="onOtherTextInput(q, $event)"
            (focus)="onInputFocus()"
            (blur)="onInputBlur()" />
    </div>
</ng-template>
